import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import {
  apiReducer,
  ApiState,
  GlobalAction,
  globalReducer,
  GlobalState,
  GLOBAL_ACTIONS,
} from '.';
import { VideoLimitStatus } from 'lib/utils/productFeature';
import { IWhiteLabel } from 'lib/api/whitelabel/getWhiteLabel';

type Props = {
  whitelabel: IWhiteLabel;
  children: ReactNode;
  unreadMessagesCount: any;
};

const ApiContext = createContext({} as { state: ApiState });
const GlobalContext = createContext({} as { state: GlobalState });
const DispatchContext = createContext(
  {} as { dispatch: React.Dispatch<GlobalAction> }
);

const initialState: GlobalState = {
  whitelabel: {} as IWhiteLabel,
  folders: [],
  library: {
    Primary: {
      videos: [],
      count: 0,
      page: 0,
      size: 10,
      search: '',
    },
  },
  currentFolder: { folderId: -2, name: 'Primary', isDeletable: false },
  dispatch: () => {},
  unreadMessagesCount: 0,
  videoLimitStatus: VideoLimitStatus.NOT_REACHED,
};

const initialApiState: ApiState = {
  firstAppLoad: 0,
  loading: false,
  error: '',
};

export const GlobalContextProvider = ({
  whitelabel,
  children,
  unreadMessagesCount,
}: Props) => {
  const [state, stateDispatch] = useReducer(globalReducer, {
    ...initialState,
    whitelabel,
    unreadMessagesCount,
  });
  const [apiState, apiDispatch] = useReducer(apiReducer, initialApiState);

  const dispatch = (props: any) => {
    stateDispatch(props);
    apiDispatch(props);
  };

  useEffect(() => {
    stateDispatch({
      type: GLOBAL_ACTIONS.SET_UNREAD_MESSAGES,
      payload: unreadMessagesCount,
    });
  }, [unreadMessagesCount]);

  return (
    <DispatchContext.Provider value={{ dispatch }}>
      <GlobalContext.Provider value={{ state }}>
        <ApiContext.Provider value={{ state: apiState }}>
          {children}
        </ApiContext.Provider>
      </GlobalContext.Provider>
    </DispatchContext.Provider>
  );
};

export const useGlobalState = () => {
  const value = useContext(GlobalContext);
  return value;
};

export const useGlobalDispatch = () => {
  const value = useContext(DispatchContext);
  return value;
};

export const useApiState = () => {
  const value = useContext(ApiContext);
  return value;
};
